import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/**
 * Toast notification system (v0.4.0 polish #23-26).
 *
 * Wraps the app in <ToastProvider>. Any descendant calls useToast() and
 * fires toast.ok / toast.err / toast.info(message). Toasts auto-dismiss
 * after 4 seconds (or 6 for errors so they're readable), portal into
 * <body>, and stack in the bottom-right of the viewport (top-right on
 * tablet+) so they never overlay important content.
 *
 * Each toast renders with the `motion-safe:animate-toast-slide` entrance
 * from tailwind, and animates out via the `closing` state's `animate-fade-out`.
 */
type ToastKind = "ok" | "err" | "info";
type Toast = {
  id: number;
  kind: ToastKind;
  message: string;
  closing?: boolean;
};

type ToastApi = {
  ok: (message: string) => void;
  err: (message: string) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: number) => {
    setToasts((cur) => cur.map((t) => (t.id === id ? { ...t, closing: true } : t)));
    setTimeout(() => {
      setToasts((cur) => cur.filter((t) => t.id !== id));
    }, 180);
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId++;
      setToasts((cur) => [...cur, { id, kind, message }]);
      const ttl = kind === "err" ? 6000 : 4000;
      const t = setTimeout(() => remove(id), ttl);
      timers.current.set(id, t);
    },
    [remove],
  );

  useEffect(() => {
    const timersMap = timers.current;
    return () => {
      timersMap.forEach((t) => clearTimeout(t));
    };
  }, []);

  const api: ToastApi = {
    ok: (m) => push("ok", m),
    err: (m) => push("err", m),
    info: (m) => push("info", m),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== "undefined"
        ? createPortal(
            <div
              aria-live="polite"
              aria-atomic="true"
              className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-4
                         sm:inset-x-auto sm:bottom-auto sm:right-6 sm:top-20 sm:items-end"
              style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
            >
              {toasts.map((t) => (
                <div
                  key={t.id}
                  role="status"
                  className={`${
                    t.kind === "ok"
                      ? "toast-ok"
                      : t.kind === "err"
                        ? "toast-err"
                        : "toast-info"
                  } ${
                    t.closing
                      ? "motion-safe:animate-fade-out"
                      : "motion-safe:animate-toast-slide"
                  }`}
                >
                  <span aria-hidden="true" className="mt-0.5 shrink-0">
                    {t.kind === "ok" ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : t.kind === "err" ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                    )}
                  </span>
                  <span className="leading-snug">{t.message}</span>
                  <button
                    type="button"
                    onClick={() => remove(t.id)}
                    aria-label="Dismiss"
                    className="ml-1 shrink-0 opacity-60 transition-opacity hover:opacity-100"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Polish v0.4.0 #27: tolerate the missing-provider case during unit
    // tests so we never throw at import time. The no-op API logs to console
    // so misconfigured trees are still discoverable.
    return {
      ok: (m) => console.info("[toast.ok]", m),
      err: (m) => console.warn("[toast.err]", m),
      info: (m) => console.info("[toast.info]", m),
    };
  }
  return ctx;
}
